// src/pages/auth/ResetPasswordPage.tsx
import React, { useEffect, useState } from 'react';
import { mapResetError } from '../../lib/authErrors.ts';
import { useAuthActions } from '../../auth/AuthProvider';
import { useAutoRedirect } from '../../hooks/useAutoRedirect';
import { useRouteTitle } from '../../hooks/useRouteTitle';

type View = 'request' | 'done' | 'invalid';

export default function ResetPasswordPage() {
  useRouteTitle("Reset Password");
  const { sendPasswordReset } = useAuthActions();
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [view] = useState<View>('request');
  const { secondsLeft, start: startRedirect } = useAutoRedirect('/login', 5);

  // Redirect link-based resets to the unified handler at /auth/action
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const mode = params.get('mode');
      const oob = params.get('oobCode');
      // Redirect both password reset AND email verification to /auth/action
      if ((mode === 'resetPassword' || mode === 'verifyEmail') && oob) {
        // Preserve the entire query string for the handler route
        const search = window.location.search.startsWith('?') ? window.location.search : `?${window.location.search}`;
        window.location.replace(`/auth/action${search}`);
      }
    } catch {
      // ignore
    }
  }, []);

  async function onRequestSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    setMsg(null);
    setSubmitting(true);
    try {
      await sendPasswordReset(email);
      setMsg('If an account exists for this email, a reset link has been sent.');
      // After notifying the user, start redirect countdown back to Sign In
      startRedirect();
    } catch (e: unknown) {
      setErr(mapResetError(e));
    } finally {
      setSubmitting(false);
    }
  }

  // reset submit handled by /auth/action; no-op here

  // Request email view (default)
  if (view === 'request') {
    return (
      <div className="mx-auto max-w-md p-6">
        <h1 className="text-2xl font-semibold mb-4 text-center text-primary">Reset your password</h1>
        <form onSubmit={onRequestSubmit} className="space-y-4">
          <input
            className="w-full rounded-xl p-3 sm:text-sm border border-orange-600 bg-slate-800 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-orange-600"
            type="email"
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {err && (
            <p className="text-red-600 text-sm font-medium bg-red-50 border border-red-200 rounded-md px-3 py-2" role="alert">{err}</p>
          )}
          {msg && (
            <div className="text-green-700 text-sm">
              <p>{msg}</p>
              <p className="mt-1 text-gray-600">Returning to Sign In in {secondsLeft}s…</p>
            </div>
          )}
          <button
            disabled={submitting}
            className="w-full rounded-2xl py-3 px-3 border border-transparent bg-accent text-primaryForeground font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-orange-600 active:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Sending…' : 'Send reset email'}
          </button>
        </form>
      </div>
    );
  }

  // New password entry no longer handled here; redirect above.

  // Invalid/expired code view
  if (view === 'invalid') {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <h1 className="text-2xl font-semibold mb-4 text-primary">Link invalid or expired</h1>
        <p className="mb-4">Please request a new reset link.</p>
  <a href="/reset-password" className="inline-block rounded-2xl px-3 py-2 border border-transparent bg-accent text-primaryForeground hover:opacity-90">Request new link</a>
      </div>
    );
  }

  // Done view
  return (
    <div className="mx-auto max-w-md p-6 text-center">
      <h1 className="text-2xl font-semibold mb-2 text-primary">Password reset successful</h1>
      <p className="mb-2">{msg || 'Your password has been reset.'}</p>
      <p className="mb-6 text-sm text-gray-600">Redirecting to Sign In in {secondsLeft}s…</p>
  <a href="/login" className="inline-block rounded-2xl px-3 py-2 border border-transparent bg-accent text-primaryForeground hover:opacity-90">Go to Sign In now</a>
    </div>
  );
}